import * as SliderPrimitive from '@radix-ui/react-slider';
import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import {
  sliderTrackVariants,
  sliderRangeVariants,
  sliderThumbVariants,
} from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';

export interface SliderProps
  extends
    React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
    VariantProps<typeof sliderTrackVariants> {
  readonly variant?: VariantProps<typeof sliderRangeVariants>['variant'];
}

const Slider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      size,
      variant,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      disabled,
      ...props
    },
    ref
  ) => (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none items-center',
        className
      )}
      {...(disabled !== undefined && { disabled })}
      {...props}
    >
      <SliderPrimitive.Track className={cn(sliderTrackVariants({ size }))}>
        <SliderPrimitive.Range
          className={cn(sliderRangeVariants({ variant }))}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        className={cn(
          sliderThumbVariants({ size }),
          // WCAG 2.2 SC 2.5.5: Ensure minimum 44x44px touch target
          'min-h-[44px] min-w-[44px]'
        )}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-disabled={disabled ? true : undefined}
      />
    </SliderPrimitive.Root>
  )
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
export { SliderSize, SliderVariant } from '@/shared/types/slider.types';
